// BehandlareSjukModal.js - Modal för massutskick vid sjuk behandlare
import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../apiClient';
import { toast } from 'react-toastify';
import './BehandlareSjukModal.css';

const BehandlareSjukModal = ({
  selectedUserId,
  selectedDate,
  clinicUsers,
  onClose
}) => {
  // State
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [userId, setUserId] = useState(selectedUserId || '');
  const [date, setDate] = useState(selectedDate || new Date().toISOString().split('T')[0]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentPatients, setSentPatients] = useState(new Set());

  // Hämta mallar
  useEffect(() => {
    apiClient.get('/api/bulk-notification/templates')
      .then(res => {
        setTemplates(res.data);
        if (res.data.length > 0) {
          setSelectedTemplateId(res.data[0].template_id);
        }
      })
      .catch(err => console.error('Fel vid hämtning av mallar:', err));
  }, []);

  // Hämta bokningar när behandlare/datum ändras
  const loadAppointments = useCallback(async () => {
    if (!userId || !date) return;

    setLoading(true);
    try {
      const res = await apiClient.get('/api/bulk-notification/appointments', {
        params: { user_id: userId, date }
      });

      // Om mall är vald, hämta förhandsgranskningar
      if (selectedTemplateId) {
        const previewRes = await apiClient.post('/api/bulk-notification/preview', {
          template_id: selectedTemplateId,
          user_id: parseInt(userId),
          date
        });

        // Matcha preview-data med bokningsdata
        const previewMap = {};
        previewRes.data.previews.forEach(p => {
          previewMap[p.appointment_id] = p;
        });

        const patientsWithMessages = res.data.appointments.map(apt => ({
          ...apt,
          message: previewMap[apt.appointment_id]?.rendered_message || '',
          subject: previewMap[apt.appointment_id]?.rendered_subject || '',
          phone: apt.patient_phone || '',
          email: apt.patient_email || ''
        }));

        setPatients(patientsWithMessages);
      } else {
        setPatients(res.data.appointments.map(apt => ({
          ...apt,
          message: '',
          phone: apt.patient_phone || '',
          email: apt.patient_email || ''
        })));
      }
    } catch (err) {
      console.error('Fel vid laddning av bokningar:', err);
      toast.error('Kunde inte ladda bokningar');
    } finally {
      setLoading(false);
    }
  }, [userId, date, selectedTemplateId]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Uppdatera enskilt patientfält
  const updatePatient = (index, field, value) => {
    setPatients(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Namn på vald behandlare (för bekräftelsedialogerna)
  const getBehandlareNamn = () => {
    const u = clinicUsers.find(u => String(u.user_id) === String(userId));
    return u ? `${u.first_name} ${u.last_name}` : 'behandlaren';
  };

  // Skicka till en patient
  const sendToPatient = async (patient, index) => {
    if (!patient.message) {
      toast.error('Meddelande saknas');
      return;
    }

    // Bekräfta före utskick. Bokningarna avbokas INTE - detta meddelar bara.
    if (!window.confirm(
      `Är du säker på att du vill skicka till ${patient.patient_name} hos ${getBehandlareNamn()}?\n\n` +
      'Bokningen avbokas inte - detta skickar endast ett meddelande.'
    )) {
      return;
    }

    setSending(true);
    try {
      const res = await apiClient.post('/api/bulk-notification/send-single', {
        appointment_id: patient.appointment_id,
        message: patient.message,
        subject: patient.subject || 'Meddelande från kliniken',
        phone: patient.phone,
        email: patient.email,
        send_sms: !!patient.phone,
        send_email: !!patient.email,
        template_id: selectedTemplateId ? parseInt(selectedTemplateId) : null
      });

      if (res.data.sms_status === 'sent' || res.data.email_status === 'sent') {
        toast.success(`Skickat till ${patient.patient_name}`);
        setSentPatients(prev => new Set([...prev, patient.appointment_id]));
      } else {
        const errors = [];
        if (res.data.sms_error) errors.push(`SMS: ${res.data.sms_error}`);
        if (res.data.email_error) errors.push(`Email: ${res.data.email_error}`);
        toast.error(`Kunde inte skicka: ${errors.join(', ') || 'Okänt fel'}`);
      }
    } catch (err) {
      console.error('Fel vid skickande:', err);
      toast.error('Fel vid skickande');
    } finally {
      setSending(false);
    }
  };

  // Skicka till alla kvarvarande patienter
  const sendToAll = async () => {
    const unsent = patients.filter(p => !sentPatients.has(p.appointment_id));
    if (unsent.length === 0) {
      toast.info('Alla patienter har redan fått meddelande');
      return;
    }

    // Kontrollera att alla har meddelanden
    const withoutMessage = unsent.filter(p => !p.message);
    if (withoutMessage.length > 0) {
      toast.error(`${withoutMessage.length} patient(er) saknar meddelande`);
      return;
    }

    // Bekräfta före massutskick. Bokningarna avbokas INTE - detta meddelar bara.
    if (!window.confirm(
      `Är du säker på att du vill skicka till ${unsent.length} patient(er) hos ${getBehandlareNamn()}?\n\n` +
      'Bokningarna avbokas inte - detta skickar endast meddelanden.'
    )) {
      return;
    }

    setSending(true);
    try {
      const template = templates.find(t => t.template_id === parseInt(selectedTemplateId));
      const res = await apiClient.post('/api/bulk-notification/send-all', {
        user_id: parseInt(userId),
        date,
        template_id: selectedTemplateId ? parseInt(selectedTemplateId) : null,
        subject: template?.subject || 'Meddelande från kliniken',
        patients: unsent.map(p => ({
          appointment_id: p.appointment_id,
          message: p.message,
          phone: p.phone,
          email: p.email
        }))
      });

      toast.success(
        `Skickat: ${res.data.sent_sms} SMS, ${res.data.sent_email} e-post. ` +
        `Misslyckades: ${res.data.failed}`
      );

      // Markera alla som skickade
      const sentIds = unsent.map(p => p.appointment_id);
      setSentPatients(prev => new Set([...prev, ...sentIds]));

    } catch (err) {
      console.error('Fel vid massutskick:', err);
      toast.error('Fel vid massutskick');
    } finally {
      setSending(false);
    }
  };

  // Formatera tid för visning
  const formatTime = (isoString) => {
    if (!isoString) return '';
    if (isoString.includes('T')) {
      return isoString.split('T')[1]?.substring(0, 5) || isoString;
    }
    return isoString;
  };

  const unsentCount = patients.length - sentPatients.size;

  return (
    <div className="behandlare-sjuk-overlay" onClick={onClose}>
      <div className="behandlare-sjuk-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bsm-header">
          <h3><i className="fas fa-user-injured"></i> Behandlare sjuk</h3>
          <button className="bsm-close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Settings Bar */}
        <div className="bsm-settings-bar">
          <div className="bsm-setting-group">
            <label>Mall:</label>
            <select
              value={selectedTemplateId}
              onChange={e => setSelectedTemplateId(e.target.value)}
            >
              <option value="">-- Välj mall --</option>
              {templates.map(t => (
                <option key={t.template_id} value={t.template_id}>
                  {t.template_name}
                </option>
              ))}
            </select>
          </div>

          <div className="bsm-setting-group">
            <label>Behandlare:</label>
            <select value={userId} onChange={e => setUserId(e.target.value)}>
              <option value="">-- Välj --</option>
              {clinicUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="bsm-setting-group">
            <label>Datum:</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>
        </div>

        {/* Patient List */}
        <div className="bsm-patient-list">
          {loading ? (
            <div className="bsm-loading">Laddar bokningar...</div>
          ) : patients.length === 0 ? (
            <div className="bsm-no-patients">
              {userId && date ? 'Inga bokningar denna dag' : 'Välj behandlare och datum'}
            </div>
          ) : (
            patients.map((patient, index) => (
              <div
                key={patient.appointment_id}
                className={`bsm-patient-card ${sentPatients.has(patient.appointment_id) ? 'sent' : ''}`}
              >
                <div className="bsm-patient-header">
                  <div className="bsm-patient-info">
                    <span className="bsm-patient-name">{patient.patient_name}</span>
                    <span className="bsm-patient-time">{formatTime(patient.start_time)}</span>
                    {patient.appointment_type && (
                      <span className="bsm-patient-type">{patient.appointment_type}</span>
                    )}
                  </div>
                  {sentPatients.has(patient.appointment_id) && (
                    <span className="bsm-sent-badge"><i className="fas fa-check"></i> Skickat</span>
                  )}
                </div>

                <div className="bsm-patient-contacts">
                  <div className="bsm-contact-field">
                    <label><i className="fas fa-phone"></i></label>
                    <input
                      type="tel"
                      value={patient.phone}
                      onChange={e => updatePatient(index, 'phone', e.target.value)}
                      placeholder="Telefonnummer"
                      disabled={sentPatients.has(patient.appointment_id)}
                    />
                  </div>
                  <div className="bsm-contact-field">
                    <label><i className="fas fa-envelope"></i></label>
                    <input
                      type="email"
                      value={patient.email}
                      onChange={e => updatePatient(index, 'email', e.target.value)}
                      placeholder="E-post"
                      disabled={sentPatients.has(patient.appointment_id)}
                    />
                  </div>
                </div>

                <div className="bsm-message-field">
                  <textarea
                    value={patient.message}
                    onChange={e => updatePatient(index, 'message', e.target.value)}
                    placeholder="Meddelande..."
                    rows={3}
                    disabled={sentPatients.has(patient.appointment_id)}
                  />
                </div>

                <div className="bsm-patient-actions">
                  <button
                    className="bsm-send-single-btn"
                    onClick={() => sendToPatient(patient, index)}
                    disabled={sending || sentPatients.has(patient.appointment_id) || !patient.message}
                  >
                    <i className="fas fa-paper-plane"></i> Skicka
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="bsm-footer">
          <div className="bsm-footer-info">
            {patients.length > 0 && (
              <span>
                {sentPatients.size} av {patients.length} skickade
              </span>
            )}
          </div>
          <div className="bsm-footer-actions">
            <button className="bsm-cancel-btn" onClick={onClose}>Stäng</button>
            <button
              className="bsm-send-all-btn"
              onClick={sendToAll}
              disabled={sending || patients.length === 0 || unsentCount === 0}
            >
              <i className="fas fa-paper-plane"></i>
              Skicka till alla ({unsentCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehandlareSjukModal;
